import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import ProductDetailPage from './pages/ProductDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AdminPage from './pages/AdminPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ExcelToolsPage from './pages/ExcelToolsPage';
import { Loader2 } from 'lucide-react';
import './index.css';
import MaintenancePage from './pages/MaintenancePage';
import axios from 'axios';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      
      {/* Legal Pages - Public without redirect */}
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/product/:id"
        element={
          <ProtectedRoute>
            <ProductDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/excel-tools"
        element={
          <AdminRoute>
            <ExcelToolsPage />
          </AdminRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function overwriteRequestsForMaintenance() {
  const originalFetch = window.fetch;

  axios.defaults.headers.common["skipMaintenance"] = "true";

  window.fetch = async (input, init = {}) => {
    const headers = new Headers(init.headers);
    console.log('🌠 fetch');

    headers.set("skipMaintenance", "true");
    console.log('🌠 headers: ', headers);

    return originalFetch(input, {
      ...init,
      headers,
    });
  };
}

async function shouldShowMaintenancePage() {
  const flags = await fetch('https://flags.mateuspitura.workers.dev/flags').then(res => res.json());
  const params = new URLSearchParams(window.location.search);
  const skipMaintenance = params.get("skipMaintenance");
  if(flags && flags['keypubli_redirect_from_emergent_to_vps']){
    if(skipMaintenance){
      overwriteRequestsForMaintenance()
    } else {
      return true;
    }
  }
  return false;
}

function App() {
  const [loading, setLoading] = useState(true);
  const [showMaintenance, setShowMaintenance] = useState(false);

  useEffect(() => {
    shouldShowMaintenancePage().then(show => {
      if(show){
        setShowMaintenance(true);
      }
    }).finally(() => {
      setLoading(false);
    })
  }, [])

  if(loading){
    return null;
  }

  if(showMaintenance){
    return <MaintenancePage/>
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <CookieConsentBanner />
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
